// RC9 background delivery extraction: submit -> short status polls -> result.
// One LOGICAL extraction = one client_key. Every HTTP retry of that submission (including after a lost
// response) reuses the key, so the server returns the SAME job instead of creating another.
import api from "@/lib/api";

// Server budget is 200 s + 30 s lease grace; an orphaned job is reclaimed and failed by a poll after that.
export const JOB_WAIT_MS = 270000;

// Sleeps, but wakes at once when the app returns to the foreground (iOS/Android suspend JS timers in the
// background), so polling resumes immediately on resume.
const sleep = (ms) => new Promise((r) => {
  const done = () => { clearTimeout(t); document.removeEventListener("visibilitychange", onVis); r(); };
  const onVis = () => { if (document.visibilityState === "visible") done(); };
  const t = setTimeout(done, ms);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
});
const statusOf = (e) => e?.normalized?.status ?? e?.response?.status;
const transient = (e) => { const s = statusOf(e); return !s || s === 0 || s === 408 || s >= 500; };

// `client` lets the Kitchen Tablet use its short-lived PIN (actor) session instead of the signed-in user's.
export async function startExtractJob({ fileId, scanIds, siteId, clientKey }, { attempts = 3, retryMs = 1500, client = api } = {}) {
  const body = { feature: "delivery_extraction", client_key: clientKey, site_id: siteId || undefined,
    ...(scanIds?.length ? { file_ids: scanIds } : { file_id: fileId }) };
  for (let i = 1; ; i += 1) {
    try {
      return (await client.post("/ai/extract-jobs", body)).data;
    } catch (e) {
      if (i >= attempts || !transient(e)) throw e;
      await sleep(retryMs);
    }
  }
}

// Polls until done/failed. Transient errors keep polling, so a reconnect resumes the same job.
// `isCancelled` stops polling only (e.g. the form closed); the job itself continues server-side.
// The wait is measured from the job's own creation time when known, so a resumed app does not restart the
// clock, and one final status read always happens after the wait (the job may have finished while suspended).
export async function pollExtractJob(jobId, { isCancelled = () => false, maxWaitMs = JOB_WAIT_MS, intervalMs = 2000, startedAt, client = api } = {}) {
  const until = (startedAt ? Date.parse(startedAt) || Date.now() : Date.now()) + maxWaitMs;
  for (let last = false; ; ) {
    last = Date.now() >= until;
    if (isCancelled()) return { status: "cancelled" };
    try {
      const { data } = await client.get(`/ai/extract-jobs/${jobId}`);
      if (data.status === "done" || data.status === "failed") return data;
    } catch (e) {
      if (!transient(e) && statusOf(e) !== 429) throw e;
    }
    if (last) break;
    await sleep(intervalMs);
  }
  return { status: "failed", error: { code: "ai_timeout", status: 504, msg: "Still no result from AI — try again or enter the details manually" } };
}

// AI fills a field only if the chef has not edited it (including deliberately clearing it).
export function mergeReference(current, mapped, touched = new Set()) {
  const pick = (f, v) => (touched.has(f) ? current[f] : (v || current[f] || ""));
  return { ...current,
    document_number: pick("document_number", mapped.document_number),
    document_date: pick("document_date", mapped.document_date),
    document_type: touched.has("document_type") ? current.document_type : (current.document_type || "delivery_note") };
}
